// SOW PDF Generation Utilities
// Generates PDF-ready HTML content for two export formats:
// 1. Report-style: Formatted sections matching Excel layout
// 2. Flat export: Simple name-value pairs for data transfer
// Uses browser print functionality for PDF generation.
// Empty sections are hidden by default (configurable via options).

import { PropertyType, getPropertyTypeDisplayText } from './sow.types';
import type {
  SOWData,
  CategoryConfig,
  PDFExportOptions,
  CurrencyValue,
} from './sow.types';
import { getCategoriesForPropertyType, sowConfig } from './sow.config';
import { formatCurrency, formatPercentage } from './currency';
import { categoryHasValues, calculateCategorySubtotal } from './calculations';

// ============================================
// TYPES
// ============================================

export interface PDFGeneratorOptions extends PDFExportOptions {
  /** Document title */
  title?: string;
  /** Show page numbers */
  showPageNumbers?: boolean;
  /** Custom CSS to inject */
  customCSS?: string;
}

// ============================================
// DEFAULT OPTIONS
// ============================================

const DEFAULT_OPTIONS: PDFGeneratorOptions = {
  type: 'report',
  includeEmptySections: false,
  emptyText: null,
  title: 'Scope of Work',
  showPageNumbers: true,
};

// ============================================
// REPORT-STYLE PDF
// ============================================

// Generate report-style PDF HTML
export function generateReportHTML(data: SOWData, options: Partial<PDFGeneratorOptions> = {}): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const categories = getCategoriesForPropertyType(data.propertyType);

  // Document title for download: "Scope of Work - <Property Address>"
  const propertyAddress = data.propertyAddress?.trim() || 'Untitled';
  const documentTitle = `Scope of Work - ${propertyAddress}`;

  let sectionsHTML = '';

  for (const category of categories) {
    const categoryData = data.categories[category.id];
    if (!categoryData) continue;

    const hasData = categoryHasValues(categoryData, data.propertyType);

    // Skip empty sections unless configured to show them
    if (!hasData && !opts.includeEmptySections) continue;

    const subtotal = calculateCategorySubtotal(categoryData, data.propertyType);

    sectionsHTML += generateCategorySectionHTML(category, categoryData, data, subtotal, hasData, opts);
  }

  // Add totals section
  sectionsHTML += generateTotalsHTML(data);

  return wrapInHTMLDocument(documentTitle, propertyAddress, sectionsHTML, opts, 'report');
}

// Generate HTML for a single category section
function generateCategorySectionHTML(
  category: CategoryConfig,
  categoryData: SOWData['categories'][string],
  data: SOWData,
  subtotal: CurrencyValue,
  hasData: boolean,
  opts: PDFGeneratorOptions
): string {
  const scopeBadge = category.scope === 'building-wide' ? 'Building-Wide' : 'Per-Unit';

  let content = '';

  if (!hasData && opts.includeEmptySections) {
    content = `<p class="empty-notice">${opts.emptyText || 'No entries'}</p>`;
  } else {
    // Description
    if (categoryData.description?.trim()) {
      content += `<div class="description"><strong>Description:</strong> ${escapeHTML(categoryData.description)}</div>`;
    }

    // Fields
    if (category.scope === 'building-wide' || data.propertyType === PropertyType.SFR) {
      content += generateBuildingWideFieldsHTML(category, categoryData);
    } else {
      content += generatePerUnitTableHTML(category, categoryData, data.unitCount);
    }
  }

  return `
    <section class="category">
      <header class="category-header">
        <h2>${escapeHTML(category.name)}</h2>
        <span class="scope-badge">${scopeBadge}</span>
        ${subtotal > 0 ? `<span class="subtotal">${formatCurrency(subtotal)}</span>` : ''}
      </header>
      <div class="category-body">
        ${content}
      </div>
    </section>
  `;
}

// Generate HTML for building-wide fields
function generateBuildingWideFieldsHTML(
  category: CategoryConfig,
  categoryData: SOWData['categories'][string]
): string {
  const rows = category.fields
    .filter(field => (categoryData.buildingWide[field.id] ?? 0) > 0)
    .map(field => `
      <tr>
        <td class="field-label">${escapeHTML(field.label)}</td>
        <td class="amount">${formatCurrency(categoryData.buildingWide[field.id] ?? 0)}</td>
      </tr>
    `)
    .join('');

  if (!rows) return '';

  return `
    <table class="fields-table">
      <thead>
        <tr>
          <th class="field-label">Line Item</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

// Generate HTML for per-unit table
function generatePerUnitTableHTML(
  category: CategoryConfig,
  categoryData: SOWData['categories'][string],
  unitCount: number
): string {
  // Build header row with proper column sizing
  const unitHeaders = Array.from({ length: unitCount }, (_, i) => `<th>Unit ${i + 1}</th>`).join('');

  // Build data rows with proper formatting
  const rows = category.fields.map(field => {
    const unitCells = Array.from({ length: unitCount }, (_, i) => {
      const value = categoryData.units[i]?.[field.id] ?? 0;
      return `<td class="amount">${value > 0 ? formatCurrency(value) : '-'}</td>`;
    }).join('');

    const buildingValue = categoryData.buildingWide[field.id] ?? 0;

    return `
      <tr>
        <td class="field-label">${escapeHTML(field.label)}</td>
        ${unitCells}
        <td class="amount">${buildingValue > 0 ? formatCurrency(buildingValue) : '-'}</td>
      </tr>
    `;
  }).join('');

  const manyUnits = unitCount >= 7;
  const manyUnitsAttr = manyUnits ? 'data-many-units="true"' : '';

  return `
    <table class="per-unit-table" ${manyUnitsAttr}>
      <thead>
        <tr>
          <th class="field-label">Line Item</th>
          ${unitHeaders}
          <th>Building</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

// Generate totals section HTML
function generateTotalsHTML(data: SOWData): string {
  const contingencyLabel = data.contingency.mode === 'auto'
    ? `Contingency (${formatPercentage(sowConfig.contingencyPercentage)})`
    : 'Contingency (Manual)';

  return `
    <section class="totals">
      <div class="totals-inner">
        <h3>Summary</h3>
        <table class="totals-table">
          <thead>
            <tr>
              <th>Description</th>
              <th class="amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Subtotal</td>
              <td class="amount">${formatCurrency(data.subtotal)}</td>
            </tr>
            <tr>
              <td>${contingencyLabel}</td>
              <td class="amount">${formatCurrency(data.contingency.value)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="grand-total">
              <th>Total Construction Cost</th>
              <td class="amount">${formatCurrency(data.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  `;
}

// ============================================
// FLAT EXPORT PDF
// ============================================

// Generate flat export PDF HTML
// Simple name-value pairs, no formatting
export function generateFlatHTML(data: SOWData, options: Partial<PDFGeneratorOptions> = {}): string {
  const opts = { ...DEFAULT_OPTIONS, ...options, type: 'flat' as const };
  const categories = getCategoriesForPropertyType(data.propertyType);

  // Document title for download: "Scope of Work - Upload - <Property Address>"
  const propertyAddress = data.propertyAddress?.trim() || 'Untitled';
  const documentTitle = `Scope of Work - Upload - ${propertyAddress}`;

  let rows = '';

  // Property info
  rows += `<tr><td>Property Address</td><td>${escapeHTML(data.propertyAddress || 'N/A')}</td></tr>`;
  rows += `<tr><td>Property Type</td><td>${escapeHTML(getPropertyTypeDisplayText(data.propertyType))}</td></tr>`;
  if (data.propertyType === PropertyType.MultiUnit) {
    rows += `<tr><td>Unit Count</td><td>${data.unitCount}</td></tr>`;
  }
  rows += '<tr class="separator"><td colspan="2"></td></tr>';

  // Category fields
  for (const category of categories) {
    const categoryData = data.categories[category.id];
    if (!categoryData) continue;

    const hasData = categoryHasValues(categoryData, data.propertyType);
    if (!hasData && !opts.includeEmptySections) continue;

    rows += `<tr class="category-header"><td colspan="2"><strong>${escapeHTML(category.name)}</strong></td></tr>`;

    // Description
    if (categoryData.description?.trim()) {
      rows += `<tr><td>${escapeHTML(category.name)} - Description</td><td>${escapeHTML(categoryData.description)}</td></tr>`;
    }

    // Building-wide fields
    for (const field of category.fields) {
      const value = categoryData.buildingWide[field.id] ?? 0;
      if (value > 0) {
        const fieldName = category.scope === 'per-unit' && data.propertyType === PropertyType.MultiUnit
          ? `${field.label} (Building)`
          : field.label;
        rows += `<tr><td>${escapeHTML(fieldName)}</td><td>${formatCurrency(value)}</td></tr>`;
      }
    }

    // Per-unit fields
    if (category.scope === 'per-unit' && data.propertyType === PropertyType.MultiUnit) {
      for (let i = 0; i < data.unitCount; i++) {
        const unitValues = categoryData.units[i];
        if (!unitValues) continue;

        for (const field of category.fields) {
          const value = unitValues[field.id] ?? 0;
          if (value > 0) {
            rows += `<tr><td>${escapeHTML(field.label)} (Unit ${i + 1})</td><td>${formatCurrency(value)}</td></tr>`;
          }
        }
      }
    }
  }

  // Totals
  rows += '<tr class="separator"><td colspan="2"></td></tr>';
  rows += `<tr><td>Subtotal</td><td>${formatCurrency(data.subtotal)}</td></tr>`;
  rows += `<tr><td>Contingency (${data.contingency.mode})</td><td>${formatCurrency(data.contingency.value)}</td></tr>`;
  rows += `<tr class="grand-total"><td><strong>Total</strong></td><td><strong>${formatCurrency(data.total)}</strong></td></tr>`;

  const content = `
    <table class="flat-table">
      <thead>
        <tr>
          <th>Field</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;

  return wrapInHTMLDocument(documentTitle, propertyAddress, content, opts, 'flat');
}

// ============================================
// HTML HELPERS
// ============================================

// Wrap content in a complete HTML document with print styles
export function wrapInHTMLDocument(
  documentTitle: string,
  propertyAddress: string,
  content: string,
  opts: PDFGeneratorOptions,
  type: 'report' | 'flat'
): string {
  const css = type === 'report' ? getReportCSS() : getFlatCSS();
  const customCSS = opts.customCSS || '';

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHTML(documentTitle)}</title>
  <style>
    ${css}
    ${customCSS}
  </style>
</head>
<body>
  ${propertyAddress ? `<header class="document-header"><div class="property-address">${escapeHTML(propertyAddress)}</div></header>` : ''}
  <main>
    ${content}
  </main>
  ${opts.showPageNumbers ? '<footer class="page-footer"></footer>' : ''}
</body>
</html>
  `.trim();
}

// Get CSS for report-style PDF
function getReportCSS(): string {
  return `
    @page {
      size: A4;
      margin: 10mm;
    }

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 9.5pt;
      line-height: 1.3;
      color: #222;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .document-header {
      text-align: left;
      margin-bottom: 8px;
      margin-top: 0;
    }

    .property-address {
      font-size: 10pt;
      font-weight: 600;
      color: #222;
    }

    .category {
      margin-bottom: 10px;
      page-break-inside: avoid;
    }

    .category-header {
      display: grid;
      grid-template-columns: 1fr auto auto;
      gap: 8px;
      align-items: center;
      margin-bottom: 6px;
      padding: 4px 6px;
      border: 1px solid #cfcfcf;
      background: #ededed;
    }

    .category-header h2 {
      font-size: 9.5pt;
      font-weight: 600;
      margin: 0;
    }

    .scope-badge {
      font-size: 8.5pt;
      color: #444;
    }

    .subtotal {
      font-size: 9.5pt;
      font-weight: 600;
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    .category-body {
      margin-top: 0;
    }

    .description {
      margin: 6px 0 8px;
      font-size: 9pt;
      line-height: 1.3;
      padding: 6px;
      background-color: #f9f9f9;
      border: 1px solid #cfcfcf;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      margin-top: 0;
      font-size: 9.5pt;
    }

    th {
      text-align: left;
      font-weight: 600;
      border: 1px solid #cfcfcf;
      background-color: #ededed;
      padding: 4px 6px;
      font-size: 9.5pt;
    }

    th.field-label {
      width: 30%;
      min-width: 200px;
    }

    th:not(.field-label) {
      text-align: right;
    }

    td {
      padding: 4px 6px;
      border: 1px solid #cfcfcf;
      vertical-align: middle;
      text-align: right;
      font-variant-numeric: tabular-nums;
      font-size: 9.5pt;
      line-height: 1.3;
    }

    td.field-label {
      text-align: left;
      font-weight: 500;
      vertical-align: middle;
      line-height: 1.3;
      padding: 4px 6px;
    }

    .per-unit-table th:not(.field-label) {
      width: 85px;
      min-width: 75px;
      max-width: 85px;
      padding: 4px 6px;
      font-size: 8.5pt;
    }

    .per-unit-table td:not(.field-label) {
      width: 85px;
      min-width: 75px;
      max-width: 85px;
      text-align: right;
      padding: 4px 6px;
      font-size: 9pt;
    }

    /* For tables with many units (7+), make columns even more compact */
    .per-unit-table[data-many-units="true"] th:not(.field-label) {
      width: 75px;
      min-width: 70px;
      max-width: 75px;
      padding: 4px 4px;
      font-size: 8pt;
    }

    .per-unit-table[data-many-units="true"] td:not(.field-label) {
      width: 75px;
      min-width: 70px;
      max-width: 75px;
      padding: 4px 4px;
      font-size: 8.5pt;
    }

    .amount {
      text-align: right !important;
      font-weight: 600;
    }

    .fields-table {
      width: 100%;
      max-width: 700px;
      margin: 0 auto;
    }

    .fields-table th.field-label {
      width: 75%;
    }

    .fields-table td {
      text-align: right;
    }

    .fields-table td.field-label {
      text-align: left;
    }

    .totals {
      margin-top: 12px;
      page-break-inside: avoid;
      display: flex;
      justify-content: flex-end;
    }

    .totals-inner {
      width: 45%;
      min-width: 280px;
    }

    .totals h3 {
      text-align: left;
      margin-bottom: 6px;
      font-size: 10pt;
      font-weight: 600;
    }

    .totals-table {
      width: 100%;
      border-collapse: collapse;
    }

    .totals-table th,
    .totals-table td {
      border: 1px solid #cfcfcf;
      padding: 4px 6px;
      font-size: 9.5pt;
    }

    .totals-table th {
      text-align: left;
      font-weight: 600;
      background: #ededed;
    }

    .totals-table td {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    .totals-table th.amount,
    .totals-table td.amount {
      text-align: right;
    }

    .totals-table td:first-child {
      text-align: left;
    }

    .grand-total {
      background: #ededed;
    }

    .grand-total th,
    .grand-total td {
      font-weight: 600;
    }

    @media print {
      body {
        margin: 0;
      }
      .page-footer {
        display: none;
      }
    }
  `;
}

// Get CSS for flat export PDF
function getFlatCSS(): string {
  return `
    @page {
      margin: 15mm;
      size: auto;
    }

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, monospace;
      font-size: 9pt;
      line-height: 1.4;
      color: #000;
    }
    
    .document-header {
      margin-bottom: 24px;
      border-bottom: 1px solid #000;
      padding-bottom: 12px;
    }
    
    h1 {
      font-size: 16pt;
      margin-bottom: 4px;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
    }
    
    th, td {
      padding: 4px 8px;
      border: 1px solid #ccc;
      vertical-align: top;
    }
    
    th {
      background: #f5f5f5;
      font-weight: 600;
      text-align: left;
    }
    
    .category-header td {
      background: #eaeaea;
      font-weight: 600;
    }
  `;
}

// Escape HTML special characters
export function escapeHTML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
